import { Stack, Typography } from '@mui/material';
import { Outlet, createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import { Suspense } from 'react';

import { useTranslation } from '@shared/i18n';

import { getProfileRoles, profileQueryOptions } from 'api/account';

import { useAccountNavigationItems } from 'component/hooks/useAccountNavigationItems';
import { IncomingAccountInvitesContainer } from 'component/partial/IncomingAccountInvitesContainer';
import { IncomingSiteTransfersContainer } from 'component/partial/IncomingSiteTransfersContainer';
import { Navigation, NavigationItem } from 'component/partial/Navigation';
import { UnpaidInvoicesAlert } from 'component/partial/UnpaidInvoicesAlert';

export const Route = createFileRoute('/n/_auth/settings/_layout')({
  beforeLoad: async ({ context, location }) => {
    const profile = await context.ensureQueryData(profileQueryOptions());
    const roles = getProfileRoles(profile);

    if (
      roles.includes('user_with_no_accounts') &&
      location.pathname !== '/n/settings/account-and-security'
    ) {
      throw redirect({
        to: '/n/settings/account-and-security',
        replace: true,
      });
    }
  },
  component: SettingsLayoutComponent,
});

function SettingsLayoutComponent() {
  const { t } = useTranslation();
  const location = useLocation();

  const navigationMenuItems: NavigationItem[] = useAccountNavigationItems()
    .filter(item => !item.menuOnly)
    .map(item => ({
      solidIcon: item.icon,
      colorIcon: item.colorIcon,
      title: item.navLabel ?? item.label,
      to: item.to,
    }));

  const isTopLevelRoute = navigationMenuItems.some(item => item.to === location.pathname);

  return (
    <Stack sx={{ gap: 6 }}>
      {isTopLevelRoute ? <Typography variant="h2">{t('common.settings')}</Typography> : null}
      <UnpaidInvoicesAlert />
      <IncomingSiteTransfersContainer />
      <IncomingAccountInvitesContainer />
      {isTopLevelRoute ? <Navigation items={navigationMenuItems} /> : null}
      <Suspense>
        <Outlet />
      </Suspense>
    </Stack>
  );
}
